import { AppContext } from '@/ui/Context';
import { appName } from '@/ui/constants';
import { backgroundCheckValueProviders } from '@/ui/features/forms';
import { useRmsFormRules } from '@/ui/features/forms/hooks';
import { useRmsFormParams } from '@/ui/hooks';
import { iconNameToIconDetailsMap } from '@/ui/utils';
import { buildAlertsValueProviders } from '@juvare/alert-ui/ui/features/forms/utils';
import { badgesCustomWidgets } from '@juvare/badging/ui/features/form/form-widgets';
import { useBadgeHistoryActionValueProvider } from '@juvare/badging/ui/features/form/hooks/useBadgeHistoryActionValueProvider';
import { useRevokedBadgeReasonsValueProvider } from '@juvare/badging/ui/features/form/hooks/useRevokedBadgeReasonsValueProvider';
import { FormConfigContext } from '@juvare/common-form-ui/FormConfigContext';
import { ValueProvider } from '@juvare/common-form-ui/ValueProvider';
import { defaultFormWidgets } from '@juvare/common-form-ui/WidgetForm';
import { icons } from '@juvare/common-form-ui/icons/icons';
import { formatPhoneNumber } from '@juvare/common-phone-number';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import SafetyCheckIcon from '@juvare/common-web-ui/icons/SafetyCheck';
import {
    contactManagementFormWidget,
    getContactManagementFormConfigRules,
    useBuildContactManagementFormValueProviders,
} from '@juvare/contact-management/ui/features/forms';
import { useContactManagementTranslation } from '@juvare/contact-management/ui/hooks/useContactManagementTranslation';
import { getOccupationFormConfigFunctionDef } from '@juvare/credentialing/backend/config/shared/platform-function-types';
import { allowViewDriverLicense } from '@juvare/credentialing/backend/features/permissions';
import { decryptDriverLicenseFunctionDef } from '@juvare/credentialing/backend/features/person-credentials/shared';
import { DriverLicense, createCredentialingFormRules } from '@juvare/credentialing/ui/features/forms';
import { useCredentialingFormParams } from '@juvare/credentialing/ui/features/forms/hooks/useCredentialingFormParams';
import { buildCredentialingSubFormProviders, buildCredentialingValueProviders } from '@juvare/credentialing/ui/features/forms/providers';
import { useCredentialingTranslation } from '@juvare/credentialing/ui/hooks';
import { credentialingWidgets } from '@juvare/credentialing/ui/widgets';
import { buildMissionsValueProviders } from '@juvare/missions/ui/forms';
import { Settings } from '@juvare/platform-sdk-react/types';
import {
    useCountyValueProvider,
    useEthnicityValueProvider,
    useGenderValueProvider,
    useRegionValueProvider,
    useRegisterStateValueProvider,
    useStateAndTerritoryValueProvider,
    useStateValueProvider,
    useTerritoryValueProvider,
} from '@juvare/platform-sdk-react/ui/features/default-classifiers';
import { DynamicColumnConfigProvider, DynamicColumnHandler } from '@juvare/platform-sdk-react/ui/features/dynamic-columns';
import { getColumnsHandlerTypeString } from '@juvare/platform-sdk-react/ui/features/dynamic-columns/utils/getColumnsHandlerTypeString';
import { getPublicSettings, getSettings } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { isUserLoggedIn } from '@juvare/platform-sdk-react/ui/stores';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import ErrorIcon from '@mui/icons-material/Error';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { Box } from '@mui/material';
import mapValues from 'lodash/mapValues';
import React from 'react';
import { useSelector } from 'react-redux';
import { useStateAndTerritoryOrEmptyArrayValueProvider } from './hooks/useStateAndTerritoryOrEmptyArrayValueProvider';

export const FormConfigContextProvider: React.FC<React.PropsWithChildren<{ googleRecaptchaV2SiteKey?: string }>> = ({
    googleRecaptchaV2SiteKey,
    children,
}) => {
    const appContext = React.useContext(AppContext);
    const userLoggedIn = useSelector(isUserLoggedIn);
    const settings = useSelector(userLoggedIn ? getSettings : getPublicSettings);
    const classifiersValueProviders: ValueProvider[][] = [
        // also used for dynamic column handlers
        [
            useCountyValueProvider(),
            useTerritoryValueProvider(),
            useStateAndTerritoryValueProvider(),
            useRegisterStateValueProvider(),
            useStateValueProvider(),
            useRegionValueProvider(),
            useStateAndTerritoryOrEmptyArrayValueProvider(),
        ],

        // used in forms only
        [useEthnicityValueProvider(), useGenderValueProvider()],
    ];

    const { t: contactManagementTranslation } = useContactManagementTranslation();
    const { t: credentialingTranslation } = useCredentialingTranslation();
    const getOccupationFormConfig = useTypedPlatformFunction(getOccupationFormConfigFunctionDef);
    const decryptDriverLicense = useTypedPlatformFunction(decryptDriverLicenseFunctionDef);
    const rmsFormRules = useRmsFormRules(appContext.userState);

    const contactManagementFormValueProviders = useBuildContactManagementFormValueProviders(
        contactManagementTranslation,
        settings as Settings,
        appContext.userState,
        appName,
    );
    const revokedBadgeReasonsValueProvider = useRevokedBadgeReasonsValueProvider();
    const badgeHistoryActionValueProvider = useBadgeHistoryActionValueProvider();

    const handlers: DynamicColumnHandler[] = classifiersValueProviders[0].map((provider) => getColumnsHandlerTypeString(provider));

    for (const provider of contactManagementFormValueProviders) {
        handlers.push(getColumnsHandlerTypeString(provider));
    }

    const showDriverLicense = (personId?: string) => {
        return allowViewDriverLicense(appContext.userState, personId);
    };

    const decryptCredentialingData = async (value: string) => {
        if (value && value !== '********') {
            return decryptDriverLicense({
                driverLicenseHash: value,
                personId: appContext?.currentUser?.userId,
            }).catch((err) => {
                handleErrorSnackbar(err);

                return value;
            });
        }

        return Promise.resolve(value);
    };

    return (
        <FormConfigContext.Provider
            value={{
                formParams: {
                    ...useCredentialingFormParams(settings, appContext.userState),
                    ...useRmsFormParams(),
                    formatPhoneNumber,
                },
                widgets: [...defaultFormWidgets, ...contactManagementFormWidget, ...credentialingWidgets, ...badgesCustomWidgets],
                valueProviders: [
                    ...backgroundCheckValueProviders,
                    ...buildCredentialingValueProviders(appName, appContext.userState),
                    ...buildMissionsValueProviders(appName, appContext.userState),
                    ...classifiersValueProviders.flat(),
                    ...contactManagementFormValueProviders,
                    ...buildAlertsValueProviders(appName),
                    revokedBadgeReasonsValueProvider,
                    badgeHistoryActionValueProvider,
                ],
                subFormProviders: buildCredentialingSubFormProviders(
                    appName,
                    credentialingTranslation,
                    getOccupationFormConfig,
                    appContext.userState,
                ),
                rules: [
                    ...getContactManagementFormConfigRules(contactManagementTranslation, settings as Settings, appContext.userState),
                    ...createCredentialingFormRules(credentialingTranslation, settings, appContext.userState),
                    ...rmsFormRules,
                ],
                globalWidgetConfig: {
                    googleRecaptchaV2SiteKey: googleRecaptchaV2SiteKey,
                    htmlOutputIcons: {
                        ...icons,
                        'icon-add-moderator': AddModeratorIcon,
                        'icon-error': ErrorIcon,
                        'icon-safety-check': SafetyCheckIcon,
                        'icon-verified-user': VerifiedUserIcon,
                    },
                    htmlOutputCustomTags: {
                        driver_license: {
                            attributes: ['dataHandler', 'dataDriverLicense', 'dataPersonId', 'dataDriverLicenseDecrypted'],
                            element: DriverLicense,
                        },
                    },
                    htmlOutputData: {
                        decryptCredentialingData: decryptCredentialingData,
                        showDriverLicense: showDriverLicense,
                    },
                    passwordDecryptFunctions: {
                        decryptDriverLicense: {
                            decryptFunction: decryptCredentialingData,
                            label: `Decrypt Driver's License Information`,
                            description: "Function that decrypts driver's license information",
                        },
                    },
                    selectIcons: {
                        ...mapValues(iconNameToIconDetailsMap, ({ Icon, color }, key) => () => (
                            <Box key={key} sx={{ display: 'flex' }}>
                                <Icon htmlColor={color} />
                            </Box>
                        )),
                    },
                },
            }}
        >
            <DynamicColumnConfigProvider columnHandlers={(defaultHandlers) => [...defaultHandlers, ...handlers]}>
                {children}
            </DynamicColumnConfigProvider>
        </FormConfigContext.Provider>
    );
};
